import React, { useState } from 'react';
import { PageHeader, Input, List, Avatar, Button, Empty, Card, Typography } from 'antd';
import { ArrowRightOutlined, UserSwitchOutlined } from '@ant-design/icons';

import { useClientRoles } from '../../../models/useClientRoles';
import { useClientRolesForUser } from '../../../models/useClientRolesForUser';
import { useClientDetails } from '../../../components/client/useClientDetails';
import { trackDelegatedSwitch, navigateToClient } from '../../../components/client/DelegatedClientsGrid';

import '../../../App.scss';
import 'antd/dist/antd.css';

const { Text } = Typography;

/** One row; loads its own client details so search can match on the real name. */
export const DelegatedClientRow = ({ id, query, onOpen }) => {
  const client = useClientDetails(id);
  if (!client) return null;

  const name = client.displayName || id;
  const q = (query || '').trim().toLowerCase();
  if (q && !name.toLowerCase().includes(q) && !id.toLowerCase().includes(q)) return null;

  return (
    <List.Item
      data-testid={`delegated-row-${id}`}
      style={{ padding: '14px 8px' }}
      actions={[
        <Button key="open" type="primary" onClick={() => onOpen(id)}>
          Open <ArrowRightOutlined />
        </Button>,
      ]}
    >
      <List.Item.Meta
        avatar={client.avatar
          ? <Avatar size={44} src={client.avatar} />
          : (
            <Avatar size={44} style={{ backgroundColor: '#7265e6', fontSize: 18 }}>
              {`${name}`.charAt(0).toUpperCase()}
            </Avatar>
          )}
        title={<Text strong style={{ fontSize: 15 }}>{name}</Text>}
        description={<Text type="secondary" style={{ fontSize: 12 }}>{id}</Text>}
      />
    </List.Item>
  );
};

/**
 * The full, searchable list behind the home strip's "All clients": every client
 * this person runs by delegation. Opening one is a FULL navigation (see
 * navigateToClient) — an SPA push would leave the dashboard on the old client.
 */
export default function DelegatedClientsScreen(props) {
  const clientId = props?.match?.params?.clientId ?? null;
  const [query, setQuery] = useState('');
  // Surface-aware like the grid: the admin route shows the VIEWED owner's
  // access (resolved from the client doc), the client route the signed-in
  // user's own. Both hooks always run; the inactive one gets null.
  const isAdminSurface = window.location.pathname.startsWith('/admin/');
  const viewedClient = useClientDetails(isAdminSurface ? clientId : null);
  const ownRoles = useClientRoles(isAdminSurface ? null : clientId);
  const ownerRoles = useClientRolesForUser(isAdminSurface ? (viewedClient ? viewedClient.ownerId : null) : null);
  const roles = isAdminSurface ? ownerRoles : ownRoles;
  const delegated = (roles || []).filter((r) => r && r.id && !r.isLoggedInClient);
  const backUrl = isAdminSurface ? `/admin/clients/${clientId}` : `/client/manage/${clientId}`;

  const openClient = (targetClientId) => {
    trackDelegatedSwitch(clientId, targetClientId);
    navigateToClient(targetClientId);
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '12px 16px 48px' }}>
      <PageHeader
        ghost={false}
        style={{ padding: '16px 0' }}
        onBack={() => props.history.push(backUrl)}
        title={<span><UserSwitchOutlined /> Client Access List{isAdminSurface ? ' — This Owner' : ''}</span>}
        subTitle={`${delegated.length}`}
      />
      <Card bodyStyle={{ padding: 16 }}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          size="large"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {delegated.length === 0 ? (
          <Empty description="No client access yet — when a client adds you as a delegated user, it appears here." />
        ) : (
          <List itemLayout="horizontal">
            {delegated.map((role) => (
              <DelegatedClientRow key={role.id} id={role.id} query={query} onOpen={openClient} />
            ))}
          </List>
        )}
      </Card>
    </div>
  );
}
